import {
  Anchor,
  AspectRatio,
  Badge,
  Flex,
  Image,
  Table,
  Text,
  useMantineColorScheme,
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import excellentDark from "../../../assets/illustrations/conditions/excellent-dark.svg";
import excellent from "../../../assets/illustrations/conditions/excellent.svg";
import fairDark from "../../../assets/illustrations/conditions/fair-dark.svg";
import fair from "../../../assets/illustrations/conditions/fair.svg";
import goodDark from "../../../assets/illustrations/conditions/good-dark.svg";
import good from "../../../assets/illustrations/conditions/good.svg";
import superbDark from "../../../assets/illustrations/conditions/superb-dark.svg";
import superb from "../../../assets/illustrations/conditions/superb.svg";

import { defaultCurrency } from "@/lib/currency";
import { formatDate } from "@/utils/formatDate";
import { formatCurrency } from "@/utils/formatEuro";

import type { ListTableRowProps } from "@/components/elements";
import type { Car, CarColumns } from "@/types/car";
import type { Currency } from "@/types/currency";
import type { FC, ReactNode } from "react";

export type CarTableRowProps = ListTableRowProps<Car, CarColumns>;

export const CarTableRow: FC<CarTableRowProps & { canAccess?: boolean }> = ({
  checkbox,
  columns,
  data,
  selected = false,
  to,
  canAccess = true,
  ...props
}) => {
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const renderCell = (column: CarColumns) => {
    try {
      switch (column) {
        case "make":
          return to ? (
            <Anchor component={Link} size="sm" to={to} onClick={(event) => event.stopPropagation()}>
              {data[column]}
            </Anchor>
          ) : (
            data[column]
          );
        case "model": {
          const modelText = data.model ?? "";
          const variantText = data.variant && data.variant.trim() !== "" ? ` ${data.variant}` : "";
          const fullText = `${modelText}${variantText}`;
          return fullText;
        }
        case "status":
          return <Badge variant="light">{t(`car.statuses.${data[column]}`, data[column])}</Badge>;
        case "condition":
          if (!data[column] || String(data[column]).trim() === "") {
            return null;
          }

          const condition = String(data[column]).toLowerCase();

          /*  
            Previous implementation using badges, now replaced with images for better visual representation 
            return <Badge variant="light">{t(`car.conditions.${data[column]}`, data[column])}</Badge>;  
          */
          switch (condition) {
            case "superb":
              return (
                <Flex direction="row" justify="space-between" align="center" gap="md" w="100%">
                  <Text fz="sm" ta="left" style={{ flex: 1, minWidth: 0 }} truncate>
                    {t(`car.conditions.${data[column]}`, data[column])}
                  </Text>
                  <AspectRatio w="2em">
                    <Image
                      src={isDark ? superbDark : superb}
                      fit="contain"
                      alt={t(`car.conditions.${condition}`, condition)}
                      aria-label={t(`car.conditions.${condition}`, condition)}
                    />
                  </AspectRatio>
                </Flex>
              );
            case "excellent":
              return (
                <Flex direction="row" justify="space-between" align="center" gap="md" w="100%">
                  <Text fz="sm" ta="left" style={{ flex: 1, minWidth: 0 }} truncate>
                    {t(`car.conditions.${data[column]}`, data[column])}
                  </Text>
                  <AspectRatio w="2em">
                    <Image
                      src={isDark ? excellentDark : excellent}
                      fit="contain"
                      alt={t(`car.conditions.${condition}`, condition)}
                      aria-label={t(`car.conditions.${condition}`, condition)}
                    />
                  </AspectRatio>
                </Flex>
              );
            case "good":
              return (
                <Flex direction="row" justify="space-between" align="center" gap="md" w="100%">
                  <Text fz="sm" ta="left" style={{ flex: 1, minWidth: 0 }} truncate>
                    {t(`car.conditions.${data[column]}`, data[column])}
                  </Text>
                  <AspectRatio w="2em">
                    <Image
                      src={isDark ? goodDark : good}
                      fit="contain"
                      alt={t(`car.conditions.${condition}`, condition)}
                      aria-label={t(`car.conditions.${condition}`, condition)}
                    />
                  </AspectRatio>
                </Flex>
              );
            case "fair":
              return (
                <Flex direction="row" justify="space-between" align="center" gap="md" w="100%">
                  <Text fz="sm" ta="left" style={{ flex: 1, minWidth: 0 }} truncate>
                    {t(`car.conditions.${data[column]}`, data[column])}
                  </Text>
                  <AspectRatio w="2em">
                    <Image
                      src={isDark ? fairDark : fair}
                      fit="contain"
                      alt={t(`car.conditions.${condition}`, condition)}
                      aria-label={t(`car.conditions.${condition}`, condition)}
                    />
                  </AspectRatio>
                </Flex>
              );
            default:
              return null;
          }

        case "purchase_price":
        case "current_value":
          if (!canAccess && column === "current_value") return null;
          return formatCurrency(data[column], currency.code);
        case "annual_gain_lost":
        case "lifetime_gain_lost":
          if (!canAccess) return null;
          return (
            <Text
              fz="sm"
              c={
                data[column] > 0
                  ? isDark
                    ? "success.4"
                    : "success.7"
                  : data[column] < 0
                    ? isDark
                      ? "danger.4"
                      : "danger.7"
                    : "var(--mantine-color-text)"
              }
            >
              {formatCurrency(data[column], currency.code)}
            </Text>
          );
        case "purchase_date":
          return formatDate(data[column]);
        default:
          return data[column] as ReactNode;
      }
    } catch (err) {
      console.log("Error:", err);
      return null;
    }
  };

  return (
    <Table.Tr
      bg={selected ? "var(--mantine-color-primary-light)" : undefined}
      style={{ cursor: "pointer" }}
      {...props}
    >
      {checkbox && (
        <Table.Td onClick={(evt) => evt.stopPropagation()} w={1}>
          {checkbox}
        </Table.Td>
      )}
      {columns.map((column) => (
        <Table.Td key={column}>{renderCell(column)}</Table.Td>
      ))}
    </Table.Tr>
  );
};
